import {
  BaseInfoAccompanyingEnum,
  BaseInfoAmbulanceWithTrainingEnum,
  BaseInfoOccupationEnum,
  BaseInfoCollaborationEnum,
  BaseInfoCollaborationOther1Enum,
  BaseInfoCollaborationOther2Enum,
  BaseInfoCollaborationTimingEnum,
  BaseInfoSexEnum,
  BaseInfoWelfareDataWelfareDistrictEnum,
  BaseInfoDrHeliRequestedTimingEnum,
  BystanderAedCountTypeEnum,
  BystanderTreatmentDataTreatmentEnum,
  BystanderTreatmentDataTreatmentPersonEnum,
  ECGWaveformEnum,
  ECGWaveRemarksEnum,
  EmsTeamMemberPositionEnum,
  FindEmsContactFacialAppearanceEnum,
  FindEmsContactFacialExpressionEnum,
  FindEmsContactPositionEnum,
  IncidentSectionEnum,
  IncidentTelegramTargetAddressTypeEnum,
  IncidentTypeEnum,
  IncidentActivityTypeEnum,
  IncidentDispatchStatusEnum,
  IncidentRoadDirectionEnum,
  IncidentRoadTypeEnum,
  MeasureTimingEnum,
  PatientCPAInitialECGEnum,
  PupilReactionEnum,
  RequestSummaryADLEnum,
  RequestSummaryADLExcretionEnum,
  RequestSummaryADLCommunicationEnum,
  RequestSummaryTransportBodyPositionEnum,
  RequestSummaryMedicalHistoryEnum,
  TreatmentInitialEcgEnum,
  TreatmentGeneralTreatmentsEnum,
  VitalO2MeanEnum,
} from 'core/gen/schemas';
import { createEnumTranslator } from 'core/utils/createEnumTranslator';

const useEnumTranslator = () => {
  /**
   * 事案種別の日本語ラベルマッピング
   */
  const INCIDENT_TYPE_LABELS = {
    [IncidentTypeEnum.FIRE]: '火災',
    [IncidentTypeEnum.NATURAL_DISASTER]: '自然災害',
    [IncidentTypeEnum.DROWNING]: '水難',
    [IncidentTypeEnum.TRAFFIC]: '交通',
    [IncidentTypeEnum.WORK_ACCIDENT]: '労働災害',
    [IncidentTypeEnum.SPORTS]: '運動競技',
    [IncidentTypeEnum.GENERAL_INJURY]: '一般負傷',
    [IncidentTypeEnum.ASSAULT]: '加害',
    [IncidentTypeEnum.SELF_INJURY]: '自損行為',
    [IncidentTypeEnum.SUDDEN_ILLNESS]: '急病',
    [IncidentTypeEnum.HOSPITAL_TRANSFER]: '転院搬送',
    [IncidentTypeEnum.HOSPITAL_TRANSFER_SUPPORT]: '転院(応援)',
    [IncidentTypeEnum.DOCTOR_TRANSPORT]: '医師搬送',
    [IncidentTypeEnum.EQUIPMENT_TRANSPORT]: '資機材輸送',
    [IncidentTypeEnum.OTHER]: 'その他',
  } as const satisfies Record<keyof typeof IncidentTypeEnum, string>;
  const getIncidentTypeLabel = createEnumTranslator(INCIDENT_TYPE_LABELS);

  /**
   * 係別の日本語ラベルマッピング
   */
  const INCIDENT_SECTION_LABELS = {
    [IncidentSectionEnum.FIRST]: '1係',
    [IncidentSectionEnum.SECOND]: '2係',
  } as const satisfies Record<keyof typeof IncidentSectionEnum, string>;
  const getIncidentSectionLabel = createEnumTranslator(INCIDENT_SECTION_LABELS);

  /**
   * 活動区分の日本語ラベルマッピング
   */
  const INCIDENT_ACTIVITY_TYPE_LABELS = {
    [IncidentActivityTypeEnum.PATIENT_TRANSPORT]: '患者搬送',
    [IncidentActivityTypeEnum.DOCTOR_TRANSPORT]: '医師搬送',
    [IncidentActivityTypeEnum.EQUIPMENT_TRANSPORT]: '資器材搬送',
    [IncidentActivityTypeEnum.NON_EMERGENCY]: '非緊急',
    [IncidentActivityTypeEnum.NO_PATIENT]: '患者なし',
    [IncidentActivityTypeEnum.REFUSAL]: '拒否',
    [IncidentActivityTypeEnum.INTOXICATION]: '酩酊',
    [IncidentActivityTypeEnum.OBVIOUS_DEATH]: '明らかな死亡',
    [IncidentActivityTypeEnum.ON_SITE_TREATMENT]: '現場処置',
    [IncidentActivityTypeEnum.FALSE_ALARM]: '誤報',
    [IncidentActivityTypeEnum.DECLINED_BEFORE_ARRIVAL]: '到着前辞退',
    [IncidentActivityTypeEnum.DECLINED_AFTER_ARRIVAL]: '到着後辞退',
    [IncidentActivityTypeEnum.TRANSPORTED_BY_OTHER_UNIT]: '他隊搬送',
    [IncidentActivityTypeEnum.OTHER]: 'その他',
  } as const satisfies Record<IncidentActivityTypeEnum, string>;
  const getIncidentActivityTypeLabel = createEnumTranslator(
    INCIDENT_ACTIVITY_TYPE_LABELS
  );

  /**
   * 受令場所の日本語ラベルマッピング
   */
  const INCIDENT_DISPATCH_STATUS_LABELS = {
    [IncidentDispatchStatusEnum.STANDBY]: '待機',
    [IncidentDispatchStatusEnum.EMERGENCY_SITE]: '緊急現場',
    [IncidentDispatchStatusEnum.IN_TRANSIT]: '搬送中',
    [IncidentDispatchStatusEnum.AFTER_HOSPITAL_ADMISSION]: '病院収容後',
    [IncidentDispatchStatusEnum.RETURNING]: '帰署中',
    [IncidentDispatchStatusEnum.ON_MISSION]: '任務中',
    [IncidentDispatchStatusEnum.OTHER]: 'その他',
  } as const satisfies Record<IncidentDispatchStatusEnum, string>;
  const getIncidentDispatchStatusLabel = createEnumTranslator(
    INCIDENT_DISPATCH_STATUS_LABELS
  );

  /**
   * 道路種別の日本語ラベルマッピング
   */
  const INCIDENT_ROAD_TYPE_LABELS = {
    [IncidentRoadTypeEnum.TOHOKU_EXPRESSWAY]: '東北自動車道',
    [IncidentRoadTypeEnum.SENDAI_EAST_ROAD]: '仙台東部道路',
    [IncidentRoadTypeEnum.SANRIKU_EXPRESSWAY]: '三陸自動車道',
    [IncidentRoadTypeEnum.SENDAI_SOUTH_ROAD]: '仙台南部道路',
    [IncidentRoadTypeEnum.SENDAI_NORTH_ROAD]: '仙台北部道路',
    [IncidentRoadTypeEnum.YAMAGATA_EXPRESSWAY]: '山形自動車道',
  } as const satisfies Record<IncidentRoadTypeEnum, string>;
  const getIncidentRoadTypeLabel = createEnumTranslator(
    INCIDENT_ROAD_TYPE_LABELS
  );

  /**
   * 道路上下の日本語ラベルマッピング
   */
  const INCIDENT_ROAD_DIRECTION_LABELS = {
    [IncidentRoadDirectionEnum.UP]: '上り',
    [IncidentRoadDirectionEnum.DOWN]: '下り',
  } as const satisfies Record<keyof typeof IncidentRoadDirectionEnum, string>;
  const getIncidentRoadDirectionLabel = createEnumTranslator(
    INCIDENT_ROAD_DIRECTION_LABELS
  );

  /**
   * 出場先が地上または地下かの日本語ラベルマッピング
   * sendaiでは OTHER, HOME, PUBLIC_PLACE, WORKPLACE, STREET は使用しない
   */
  const INCIDENT_TELEGRAM_TARGET_ADDRESS_TYPE_LABELS = {
    [IncidentTelegramTargetAddressTypeEnum.ABOVE_GROUND]: '地上',
    [IncidentTelegramTargetAddressTypeEnum.UNDERGROUND]: '地下',
  } as const satisfies Record<
    Exclude<
      keyof typeof IncidentTelegramTargetAddressTypeEnum,
      'OTHER' | 'HOME' | 'PUBLIC_PLACE' | 'WORKPLACE' | 'STREET'
    >,
    string
  >;
  const getIncidentTelegramTargetAddressTypeLabel = createEnumTranslator(
    INCIDENT_TELEGRAM_TARGET_ADDRESS_TYPE_LABELS
  );

  /**
   * 救急隊員の役職の日本語ラベルマッピング
   * sendaiでは SUB_LEADER は使用しない
   */
  const EMS_TEAM_MEMBER_POSITION_LABELS = {
    [EmsTeamMemberPositionEnum.LEADER]: '隊長',
    [EmsTeamMemberPositionEnum.MEMBER]: '隊員',
  } as const satisfies Record<
    Exclude<keyof typeof EmsTeamMemberPositionEnum, 'SUB_LEADER'>,
    string
  >;
  const getEmsTeamMemberPositionLabel = createEnumTranslator(
    EMS_TEAM_MEMBER_POSITION_LABELS,
    '不明'
  );

  /**
   * 傷病者の性別の日本語ラベルマッピング
   */
  const PATIENT_SEX_LABELS = {
    [BaseInfoSexEnum.MALE]: '男',
    [BaseInfoSexEnum.FEMALE]: '女',
    [BaseInfoSexEnum.UNKNOWN]: '不明',
  } as const satisfies Record<keyof typeof BaseInfoSexEnum, string>;
  const getPatientSexLabel = createEnumTranslator(PATIENT_SEX_LABELS);

  /**
   * 職業の日本語ラベルマッピング
   * sendaiでは STUDENT, PART_TIME_WORKER, CHILD, INFANT, HOMEMAKER は使用しない
   */
  const OCCUPATION_LABELS = {
    [BaseInfoOccupationEnum.UNEMPLOYED]: '無職',
    [BaseInfoOccupationEnum.EMPLOYEE]: '会社員',
    [BaseInfoOccupationEnum.GOVERNMENT]: '公務員',
    [BaseInfoOccupationEnum.SELF]: '自営業',
    [BaseInfoOccupationEnum.UNIVERSITY_STUDENT]: '大学生',
    [BaseInfoOccupationEnum.HOUSEWIFE]: '主婦',
    [BaseInfoOccupationEnum.ELEMENTARY_SCHOOL_STUDENT]: '小学生',
    [BaseInfoOccupationEnum.HIGH_SCHOOL_STUDENT]: '高校生',
    [BaseInfoOccupationEnum.MIDDLE_SCHOOL_STUDENT]: '中学生',
    [BaseInfoOccupationEnum.MEDICAL]: '医療関係',
    [BaseInfoOccupationEnum.VOCATIONAL_SCHOOL_STUDENT]: '各種学校生',
    [BaseInfoOccupationEnum.NONPROFIT]: '団体職員',
    [BaseInfoOccupationEnum.SERVICE_WORKER]: 'サービス業',
    [BaseInfoOccupationEnum.TECHNICAL_COLLEGE_STUDENT]: '高等専門学校生',
    [BaseInfoOccupationEnum.FREELANCER]: '自由業',
    [BaseInfoOccupationEnum.MAFF]: '農林漁業',
    [BaseInfoOccupationEnum.JUNIOR_COLLEGE_STUDENT]: '短大生',
    [BaseInfoOccupationEnum.LABORER]: '労働者',
    [BaseInfoOccupationEnum.OTHER]: 'その他',
    [BaseInfoOccupationEnum.UNKNOWN]: '不明',
  } as const satisfies Record<
    Exclude<
      keyof typeof BaseInfoOccupationEnum,
      'STUDENT' | 'PART_TIME_WORKER' | 'CHILD' | 'INFANT' | 'HOMEMAKER'
    >,
    string
  >;

  const getOccupationLabel = createEnumTranslator(OCCUPATION_LABELS);

  /**
   * ADL（日常生活動作）の日本語ラベルマッピング
   */
  const ADL_LABELS = {
    [RequestSummaryADLEnum.INDEPENDENT]: '自立',
    [RequestSummaryADLEnum.CARE_REQUIRED]: '介護あり',
    [RequestSummaryADLEnum.BEDRIDDEN]: '寝たきり',
    [RequestSummaryADLEnum.PARTIAL_ASSISTANCE]: '一部介助',
    [RequestSummaryADLEnum.FULL_ASSISTANCE]: '全介助',
    [RequestSummaryADLEnum.UNKNOWN]: '不明',
  } as const satisfies Record<keyof typeof RequestSummaryADLEnum, string>;
  const getADLLabel = createEnumTranslator(ADL_LABELS);

  /**
   * ADL排泄の日本語ラベルマッピング
   */
  const ADL_EXCRETION_LABELS = {
    [RequestSummaryADLExcretionEnum.INDEPENDENT]: '自立',
    [RequestSummaryADLExcretionEnum.ASSISTANCE]: '介助',
    [RequestSummaryADLExcretionEnum.DIAPER]: 'オムツ',
  } as const satisfies Record<
    keyof typeof RequestSummaryADLExcretionEnum,
    string
  >;
  const getADLExcretionLabel = createEnumTranslator(ADL_EXCRETION_LABELS);

  /**
   * ADL意思疎通の日本語ラベルマッピング
   */
  const ADL_COMMUNICATION_LABELS = {
    [RequestSummaryADLCommunicationEnum.POSSIBLE]: '可能',
    [RequestSummaryADLCommunicationEnum.PARTIAL]: '一部',
    [RequestSummaryADLCommunicationEnum.IMPOSSIBLE]: '不可能',
  } as const satisfies Record<
    keyof typeof RequestSummaryADLCommunicationEnum,
    string
  >;
  const getADLCommunicationLabel = createEnumTranslator(
    ADL_COMMUNICATION_LABELS
  );

  /**
   * 搬送体位の日本語ラベルマッピング
   */
  const TRANSPORT_BODY_POSITION_LABELS = {
    [RequestSummaryTransportBodyPositionEnum.SUPINE]: '仰臥位',
    [RequestSummaryTransportBodyPositionEnum.LATERAL]: '側臥位',
    [RequestSummaryTransportBodyPositionEnum.SITTING]: '坐位',
    [RequestSummaryTransportBodyPositionEnum.SEMI_SITTING]: '半坐位',
    [RequestSummaryTransportBodyPositionEnum.PRONE]: '腹臥位',
    [RequestSummaryTransportBodyPositionEnum.KNEE_FLEXION]: '膝屈曲位',
    [RequestSummaryTransportBodyPositionEnum.SHOCK]: 'ショック体位',
    [RequestSummaryTransportBodyPositionEnum.STANDING]: '立位',
    [RequestSummaryTransportBodyPositionEnum.OTHER]: 'その他',
  } as const satisfies Record<
    keyof typeof RequestSummaryTransportBodyPositionEnum,
    string
  >;
  const getTransportBodyPositionLabel = createEnumTranslator(
    TRANSPORT_BODY_POSITION_LABELS
  );

  /**
   * 既往歴の日本語ラベルマッピング
   * sendaiでは一部の項目は使用しない
   */
  const MEDICAL_HISTORY_LABELS = {
    [RequestSummaryMedicalHistoryEnum.HYPER_TENSION]: '高血圧',
    [RequestSummaryMedicalHistoryEnum.HYPER_LIPIDEMIA]: '高脂血症',
    [RequestSummaryMedicalHistoryEnum.COPD]: 'COPD',
    [RequestSummaryMedicalHistoryEnum.DIABETES]: '糖尿病',
    [RequestSummaryMedicalHistoryEnum.MYOCARDIAL_INFARCTION]: '心筋梗塞',
    [RequestSummaryMedicalHistoryEnum.CANCER]: '癌',
    [RequestSummaryMedicalHistoryEnum.CEREBRAL_INFARCTION]: '脳梗塞',
    [RequestSummaryMedicalHistoryEnum.DEMENTIA]: '認知症',
    [RequestSummaryMedicalHistoryEnum.EPILEPSY]: 'てんかん',
    [RequestSummaryMedicalHistoryEnum.ANGINA]: '狭心症',
    [RequestSummaryMedicalHistoryEnum.KIDNEY_FAILURE]: '腎不全',
    [RequestSummaryMedicalHistoryEnum.HEART_FAILURE]: '心不全',
    [RequestSummaryMedicalHistoryEnum.DIALYSIS]: '透析',
    [RequestSummaryMedicalHistoryEnum.MENTAL_ILLNESS]: '精神疾患',
    [RequestSummaryMedicalHistoryEnum.ASTHMA]: '喘息',
  } as const satisfies Record<
    Exclude<
      keyof typeof RequestSummaryMedicalHistoryEnum,
      | 'ARRHYTHMIA'
      | 'OTHER_HEART_DISEASES'
      | 'CEREBROVASCULAR'
      | 'RESPIRATORY_DISEASES'
      | 'LIVER_DISEASES'
      | 'KIDNEY_DISEASES'
      | 'DIGESTIVE_DISEASES'
      | 'CEREBRAL_HEMORRHAGE'
      | 'CIRCULATORY_SYSTEM'
      | 'NERVOUS_SYSTEM'
      | 'ENDOCRINE_SYSTEM'
      | 'HEMATOLOGY'
      | 'OBSTETRICS_AND_GYNECOLOGY'
      | 'INFECTIOUS_DISEASES'
      | 'RENAL_UROLOGY'
      | 'ALLERGY'
      | 'OTHER'
    >,
    string
  >;

  const getMedicalHistoryLabel = createEnumTranslator(MEDICAL_HISTORY_LABELS);

  /**
   * 心電図波形の日本語ラベルマッピング
   * sendaiでは NORMAL, ARRHYTHMIA, BUNDLE_BRANCH_BLOCK, ST_ELEVATION, ST_DEPRESSION, VPC は使用しない
   */
  const ECG_WAVEFORM_LABELS = {
    [ECGWaveformEnum.NONE]: 'なし',
    [ECGWaveformEnum.NORMAL]: '正常',
    [ECGWaveformEnum.SINUS_RHYTHM]: '洞調律',
    [ECGWaveformEnum.SINUS_BRADYCARDIA]: '洞徐脈',
    [ECGWaveformEnum.SINUS_TACHYCARDIA]: '洞頻脈',
    [ECGWaveformEnum.ASYSTOLE]: '心静止',
    [ECGWaveformEnum.ARRHYTHMIA]: 'リズム不整',
    [ECGWaveformEnum.PEA]: 'PEA',
    [ECGWaveformEnum.VENTRICULAR_FIBRILLATION]: '心室細動',
    [ECGWaveformEnum.VENTRICULAR_TACHYCARDIA]: '心室頻拍',
    [ECGWaveformEnum.PULSELESS_VT]: '無脈性VT',
    [ECGWaveformEnum.ATRIAL_FIBRILLATION]: '心房細動',
    [ECGWaveformEnum.ATRIAL_FLUTTER]: '心房粗動',
    [ECGWaveformEnum.PSVT]: '上室性頻拍',
    [ECGWaveformEnum.AV_BLOCK]: '房室ブロック',
    [ECGWaveformEnum.FIRST_DEGREE_AV_BLOCK]: 'Ⅰ度房室ブロック',
    [ECGWaveformEnum.SECOND_DEGREE_AV_BLOCK]: 'Ⅱ度房室ブロック',
    [ECGWaveformEnum.THIRD_DEGREE_AV_BLOCK]: 'Ⅲ度房室ブロック',
    [ECGWaveformEnum.BUNDLE_BRANCH_BLOCK]: '脚ブロック',
    [ECGWaveformEnum.BRADYCARDIA]: '徐脈性不整脈',
    [ECGWaveformEnum.TACHYCARDIA]: '頻脈性不整脈',
    [ECGWaveformEnum.ST_ELEVATION]: 'STの上昇',
    [ECGWaveformEnum.ST_DEPRESSION]: 'STの下降',
    [ECGWaveformEnum.PVC]: '心室性期外収縮',
    [ECGWaveformEnum.VPC]: '心室性期外収縮',
    [ECGWaveformEnum.OTHER]: 'その他',
  } as const satisfies Record<ECGWaveformEnum, string>;

  const getECGWaveformLabel = createEnumTranslator(ECG_WAVEFORM_LABELS);

  /**
   * 心電図備考の日本語ラベルマッピング
   */
  const ECG_REMARKS_LABELS = {
    [ECGWaveRemarksEnum.ST_ELEVATION]: 'ST上昇',
    [ECGWaveRemarksEnum.ST_DEPRESSION]: 'ST低下',
    [ECGWaveRemarksEnum.PVC]: '心室性期外収縮',
    [ECGWaveRemarksEnum.PAC]: '上室性期外収縮',
    [ECGWaveRemarksEnum.OTHER]: 'その他',
  } as const satisfies Record<keyof typeof ECGWaveRemarksEnum, string>;
  const getECGRemarksLabel = createEnumTranslator(ECG_REMARKS_LABELS);

  /**
   * バイタル測定タイミングの日本語ラベルマッピング
   * 注: FlutterのbeforeHospitalArrival（病着前）はAPIスキーマに未定義
   */
  const MEASURE_TIMING_LABELS = {
    [MeasureTimingEnum.CONTACT]: '接触時',
    [MeasureTimingEnum.SITE]: '現場',
    [MeasureTimingEnum.AMBULANCE_ACCOMMODATION]: '車内収容時',
    [MeasureTimingEnum.IN_AMBULANCE]: '車内',
    [MeasureTimingEnum.TRANSPORTING]: '搬送途上',
    [MeasureTimingEnum.HOSPITAL_ARRIVAL]: '病着時',
    [MeasureTimingEnum.BEFORE_HOSPITAL_ARRIVAL]: '病着前',
    [MeasureTimingEnum.SITE_DEPARTURE]: '現場出発',
    [MeasureTimingEnum.OTHER]: 'その他',
  } as const satisfies Record<MeasureTimingEnum, string>;

  const getMeasureTimingLabel = createEnumTranslator(MEASURE_TIMING_LABELS);

  /**
   * 瞳孔対光反射の日本語ラベルマッピング
   * sendaiでは SLUGGISH は使用しない
   */
  const PUPIL_REACTION_LABELS = {
    [PupilReactionEnum.POSITIVE]: 'あり',
    [PupilReactionEnum.NEGATIVE]: 'なし',
    [PupilReactionEnum.UNKNOWN]: '不明',
  } as const satisfies Record<
    Exclude<keyof typeof PupilReactionEnum, 'SLUGGISH'>,
    string
  >;
  const getPupilReactionLabel = createEnumTranslator(PUPIL_REACTION_LABELS);

  /**
   * 酸素投与方法の日本語ラベルマッピング
   * 注: VENTILATORはFlutterに存在しないがAPIにあるためマッピング
   */
  const O2_MEANS_LABELS = {
    [VitalO2MeanEnum.NASAL_CANNULA]: '鼻カニューレ',
    [VitalO2MeanEnum.FACE_MASK]: 'フェイスマスク',
    [VitalO2MeanEnum.VENTURI]: 'ベンチュリー',
    [VitalO2MeanEnum.RESERVOIR_MASK]: 'リザーバーマスク',
    [VitalO2MeanEnum.BVM]: 'BVM',
    [VitalO2MeanEnum.VENTILATOR]: '人工呼吸器',
    [VitalO2MeanEnum.OTHER]: 'その他',
    [VitalO2MeanEnum.NONE]: 'なし',
  } as const satisfies Record<VitalO2MeanEnum, string>;

  const getO2MeansLabel = createEnumTranslator(O2_MEANS_LABELS);

  /**
   * 初期ECGの日本語ラベルマッピング
   */
  const INITIAL_ECG_LABELS = {
    [TreatmentInitialEcgEnum.NORMAL]: '正常',
    [TreatmentInitialEcgEnum.VF]: 'Vf',
    [TreatmentInitialEcgEnum.VT]: 'VT',
    [TreatmentInitialEcgEnum.PEA]: 'PEA',
    [TreatmentInitialEcgEnum.ASYSTOLE]: 'Asystole',
    [TreatmentInitialEcgEnum.OTHER]: '他',
  } as const satisfies Record<keyof typeof TreatmentInitialEcgEnum, string>;
  const getInitialECGLabel = createEnumTranslator(INITIAL_ECG_LABELS);

  /**
   * 顔貌の日本語ラベルマッピング
   */
  const FACIAL_APPEARANCE_LABELS = {
    [FindEmsContactFacialAppearanceEnum.NORMAL]: '正常',
    [FindEmsContactFacialAppearanceEnum.JAUNDICE]: '黄疸',
    [FindEmsContactFacialAppearanceEnum.FLUSHING]: '紅潮',
    [FindEmsContactFacialAppearanceEnum.PALE]: '蒼白',
    [FindEmsContactFacialAppearanceEnum.ASHEN]: '土気色',
    [FindEmsContactFacialAppearanceEnum.CYANOSIS]: 'チアノーゼ',
    [FindEmsContactFacialAppearanceEnum.SWEATING]: '発汗',
    [FindEmsContactFacialAppearanceEnum.COLD_SWEAT]: '冷汗',
    [FindEmsContactFacialAppearanceEnum.OTHER]: 'その他',
  } as const satisfies Record<
    keyof typeof FindEmsContactFacialAppearanceEnum,
    string
  >;
  const getFacialAppearanceLabel = createEnumTranslator(
    FACIAL_APPEARANCE_LABELS
  );

  /**
   * 表情の日本語ラベルマッピング
   */
  const FACIAL_EXPRESSION_LABELS = {
    [FindEmsContactFacialExpressionEnum.NORMAL]: '正常',
    [FindEmsContactFacialExpressionEnum.AGONY]: '苦悶',
    [FindEmsContactFacialExpressionEnum.EXCITEMENT]: '興奮',
    [FindEmsContactFacialExpressionEnum.BLANK]: '無表情',
    [FindEmsContactFacialExpressionEnum.CRYING]: '泣く',
    [FindEmsContactFacialExpressionEnum.VACANT]: 'うつろ様',
    [FindEmsContactFacialExpressionEnum.ANXIOUS]: '不安様',
    [FindEmsContactFacialExpressionEnum.OTHER]: 'その他',
  } as const satisfies Record<
    keyof typeof FindEmsContactFacialExpressionEnum,
    string
  >;
  const getFacialExpressionLabel = createEnumTranslator(
    FACIAL_EXPRESSION_LABELS
  );

  /**
   * 体位の日本語ラベルマッピング
   */
  const POSITION_LABELS = {
    [FindEmsContactPositionEnum.SUPINE]: '仰臥位',
    [FindEmsContactPositionEnum.PRONE]: '腹臥位',
    [FindEmsContactPositionEnum.LATERAL]: '側臥位',
    [FindEmsContactPositionEnum.LATERAL_RIGHT]: '側臥位 右',
    [FindEmsContactPositionEnum.LATERAL_LEFT]: '側臥位 左',
    [FindEmsContactPositionEnum.SITTING]: '坐位',
    [FindEmsContactPositionEnum.SEMI_SITTING]: '半坐位',
    [FindEmsContactPositionEnum.KNEE_FLEXION]: '膝屈曲',
    [FindEmsContactPositionEnum.SHOCK]: 'ショック',
    [FindEmsContactPositionEnum.STANDING]: '立位',
    [FindEmsContactPositionEnum.OTHER]: 'その他',
  } as const satisfies Record<keyof typeof FindEmsContactPositionEnum, string>;
  const getPositionLabel = createEnumTranslator(POSITION_LABELS);

  /**
   * 同乗実習生区分の日本語ラベルマッピング
   */
  const AMBULANCE_WITH_TRAINING_LABELS = {
    [BaseInfoAmbulanceWithTrainingEnum.MEDICAL_STUDENT]: '医学生',
    [BaseInfoAmbulanceWithTrainingEnum.DEFENSE_FORCES]: '自衛隊',
  } as const satisfies Record<
    keyof typeof BaseInfoAmbulanceWithTrainingEnum,
    string
  >;

  /**
   * 福祉区分の日本語ラベルマッピング（保健福祉センター扱）
   */
  const WELFARE_DISTRICT_LABELS = {
    [BaseInfoWelfareDataWelfareDistrictEnum.AOBA]: '青葉',
    [BaseInfoWelfareDataWelfareDistrictEnum.MIYAGINO]: '宮城野',
    [BaseInfoWelfareDataWelfareDistrictEnum.WAKABAYASHI]: '若林',
    [BaseInfoWelfareDataWelfareDistrictEnum.TAIHAKU]: '太白',
    [BaseInfoWelfareDataWelfareDistrictEnum.IZUMI]: '泉',
    [BaseInfoWelfareDataWelfareDistrictEnum.MIYAGI_BRANCH]: '宮城支所',
    [BaseInfoWelfareDataWelfareDistrictEnum.AKIHO_BRANCH]: '秋保支所',
  } as const satisfies Record<
    keyof typeof BaseInfoWelfareDataWelfareDistrictEnum,
    string
  >;
  const getWelfareDistrictLabel = createEnumTranslator(WELFARE_DISTRICT_LABELS);

  const getAmbulanceWithTrainingLabel = createEnumTranslator(
    AMBULANCE_WITH_TRAINING_LABELS
  );

  /**
   * CPA初期心電図の日本語ラベルマッピング
   */
  const CPA_INITIAL_ECG_LABELS = {
    [PatientCPAInitialECGEnum.VF]: 'Vf',
    [PatientCPAInitialECGEnum.VT]: 'VT',
    [PatientCPAInitialECGEnum.PEA]: 'PEA',
    [PatientCPAInitialECGEnum.ASYSTOLE]: 'Asystole',
    [PatientCPAInitialECGEnum.OTHER]: '他',
  } as const satisfies Record<keyof typeof PatientCPAInitialECGEnum, string>;
  const getCpaInitialEcgLabel = createEnumTranslator(CPA_INITIAL_ECG_LABELS);

  /**
   * 一般処置の日本語ラベルマッピング
   */
  const GENERAL_TREATMENTS_LABELS = {
    [TreatmentGeneralTreatmentsEnum.MONITOR]: 'モニタ',
    [TreatmentGeneralTreatmentsEnum.ECG]: '心電図測定',
    [TreatmentGeneralTreatmentsEnum.O2]: '酸素',
    [TreatmentGeneralTreatmentsEnum.SUCTION]: '吸引',
    [TreatmentGeneralTreatmentsEnum.FOREIGN_BODY_REMOVAL]: '異物除去',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_ESTABLISHMENT_MANUAL]:
      '気道確保１(用手)',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_ESTABLISHMENT_BVM]: 'BVM',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_ORAL]: 'エアウェイ(経口)',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_NASAL]: '気道確保２(経鼻エアウェイ)',
    [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_MANUAL]: '胸骨圧迫(用手)',
    [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_AUTO]: '胸骨圧迫(自動)',
    [TreatmentGeneralTreatmentsEnum.HEMOSTASIS]: '止血',
    [TreatmentGeneralTreatmentsEnum.COVERING]: '被覆',
    [TreatmentGeneralTreatmentsEnum.WOUND_CARE]: '創処置',
    [TreatmentGeneralTreatmentsEnum.CERVICAL_COLLAR]: '頸椎カラー',
    [TreatmentGeneralTreatmentsEnum.SCOOP_STRETCHER]: '全身固定',
    [TreatmentGeneralTreatmentsEnum.PELVIC_STABILIZATION_SHEET]: 'シーツ',
    [TreatmentGeneralTreatmentsEnum.PELVIC_STABILIZATION_INSTRUMENT]: '器具',
    [TreatmentGeneralTreatmentsEnum.DEFIBRILLATION]: '除細動',
    [TreatmentGeneralTreatmentsEnum.ECG_TRANSMISSION]: '小電池伝送',
    [TreatmentGeneralTreatmentsEnum.AUSCULTATION]: '心音・呼吸音等の聴取',
    [TreatmentGeneralTreatmentsEnum.O2_HIGH]: '酸素(高)',
    [TreatmentGeneralTreatmentsEnum.O2_MEDIUM]: '酸素(中)',
    [TreatmentGeneralTreatmentsEnum.O2_LOW]: '酸素(低)',
    [TreatmentGeneralTreatmentsEnum.SPO2_MEASUREMENT]: '血中酸素飽和度の測定',
    [TreatmentGeneralTreatmentsEnum.DRUG_ADMINISTRATION]: '薬剤投与',
    [TreatmentGeneralTreatmentsEnum.GLUCOSE_ADMINISTRATION]: 'ブドウ糖投与',
    [TreatmentGeneralTreatmentsEnum.EPIPEN_ADMINISTRATION]: 'エピペン投与',
    [TreatmentGeneralTreatmentsEnum.GLUCOSE_MEASUREMENT]: '血糖測定',
    [TreatmentGeneralTreatmentsEnum.BT_MEASUREMENT]: '体温測定',
    [TreatmentGeneralTreatmentsEnum.BP_MEASUREMENT]: '血圧測定',
    [TreatmentGeneralTreatmentsEnum.BP_MAINTENANCE_WITH_SHOCK_PANTS]:
      'ショックパンツによる血圧保持',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_MANUAL]: '気道確保(用手)',
    [TreatmentGeneralTreatmentsEnum.VENTILATION_MANUAL]: '人工呼吸１(用手)',
    [TreatmentGeneralTreatmentsEnum.VENTILATION_AUTOMATIC]:
      '人工呼吸２(自動式人工呼吸器)',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_LARYNGOSCOPE_FORCEPS_SUCTION]:
      '気道確保３(喉頭鏡、鉗子、吸引)',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_ESOPHAGEAL_OR_OROPHARYNGEAL_TUBE]:
      '気道確保４(食道閉鎖式・口腔咽頭チューブ)',
    [TreatmentGeneralTreatmentsEnum.AIRWAY_INTUBATION]: '気道確保５(気管挿管)',
    [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_AUTO_ON_SITE]:
      '胸骨圧迫(自動・現場)',
    [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_AUTO_IN_VEHICLE]:
      '胸骨圧迫(自動・車内)',
    [TreatmentGeneralTreatmentsEnum.IV_LINE_ESTABLISHMENT_INFUSION]:
      '静脈路確保(点滴)',
    [TreatmentGeneralTreatmentsEnum.IV_ACCESS_BEFORE_CPA_GLUCOSE]:
      '心肺停止前の静脈路確保(ブドウ糖投与有)',
    [TreatmentGeneralTreatmentsEnum.IV_ACCESS_BEFORE_CPA_SHOCK]:
      '心肺停止中の静脈路確保(ショック)',
    [TreatmentGeneralTreatmentsEnum.HEMOSTASIS_PRESSURE]: '止血(圧迫)',
    [TreatmentGeneralTreatmentsEnum.HEMOSTASIS_DEVICE]: '止血(器具)',
    [TreatmentGeneralTreatmentsEnum.POSITION_MANAGEMENT]: '体位管理',
    [TreatmentGeneralTreatmentsEnum.IMMOBILIZATION]: '固定',
    [TreatmentGeneralTreatmentsEnum.HEAD_STABILIZATION]: '頭部固定',
    [TreatmentGeneralTreatmentsEnum.LOCAL_STABILIZATION]: '局所固定',
    [TreatmentGeneralTreatmentsEnum.WARMING]: '保温',
    [TreatmentGeneralTreatmentsEnum.COOLING]: '冷却',
    [TreatmentGeneralTreatmentsEnum.HOME_TREATMENT_INFUSION]:
      '在宅傷病１(点高)',
    [TreatmentGeneralTreatmentsEnum.HOME_TREATMENT_EXTERNAL_FISTULA]:
      '在宅傷病２(外陰機瘦)',
    [TreatmentGeneralTreatmentsEnum.HOME_TREATMENT_OTHER]: 'その他在宅医療指法',
    [TreatmentGeneralTreatmentsEnum.OTHER]: 'その他',
  } as const satisfies Record<TreatmentGeneralTreatmentsEnum, string>;
  const getGeneralTreatmentsLabel = createEnumTranslator(
    GENERAL_TREATMENTS_LABELS
  );

  /**
   * 同乗者区分の日本語ラベルマッピング
   */
  const ACCOMPANYING_LABELS = {
    [BaseInfoAccompanyingEnum.RELATIVE]: '親族',
    [BaseInfoAccompanyingEnum.COLLEAGUE]: '同僚',
    [BaseInfoAccompanyingEnum.FAMILY]: '家族',
    [BaseInfoAccompanyingEnum.DOCTOR]: '医師',
    [BaseInfoAccompanyingEnum.NURSE]: '看護師',
    [BaseInfoAccompanyingEnum.ACQUAINTANCE]: '関係者',
    [BaseInfoAccompanyingEnum.POLICEMAN]: '警察官',
    [BaseInfoAccompanyingEnum.EMS_TEAM]: '救急隊',
    [BaseInfoAccompanyingEnum.FIREFIGHTING_TEAM]: '消防隊',
    [BaseInfoAccompanyingEnum.PUBLIC_HEALTH_NURSE]: '保健師',
    [BaseInfoAccompanyingEnum.MIDWIFE]: '助産師',
    [BaseInfoAccompanyingEnum.HELPER]: 'ヘルパー等',
    [BaseInfoAccompanyingEnum.FACILITY_STAFF]: '施設職員',
    [BaseInfoAccompanyingEnum.TEACHER]: '教職員',
    [BaseInfoAccompanyingEnum.PASSERBY]: '通行人',
    [BaseInfoAccompanyingEnum.FIRE_BRIGADE_MEMBER]: '消防団員',
    [BaseInfoAccompanyingEnum.OTHER]: 'その他',
  } as const satisfies Record<BaseInfoAccompanyingEnum, string>;

  const getAccompanyingLabel = createEnumTranslator(ACCOMPANYING_LABELS);

  /**
   * バイスタンダー実施者区分
   */
  const BYSTANDER_TYPE_LABELS = {
    HEALTHCARE_PROVIDER: '医療従事者',
    CERTIFIED_FIRST_RESPONDER: '認定救命士',
    FIRE_DEPARTMENT_TRAINEE: '消防訓練生',
    OTHER: 'その他',
    NONE: 'なし',
    UNKNOWN: '不明',
  } as const;
  const getBystanderTypeLabel = createEnumTranslator(BYSTANDER_TYPE_LABELS);

  /**
   * 市民応急処置（バイスタンダー処置）のラベル
   */
  const BYSTANDER_TREATMENT_LABELS = {
    [BystanderTreatmentDataTreatmentEnum.COVERING]: '被覆',
    [BystanderTreatmentDataTreatmentEnum.POSITION_MANAGEMENT]: '体位管理',
    [BystanderTreatmentDataTreatmentEnum.WARMING]: '保温',
    [BystanderTreatmentDataTreatmentEnum.COOLING]: '冷却',
    [BystanderTreatmentDataTreatmentEnum.IMMOBILIZATION]: '固定',
    [BystanderTreatmentDataTreatmentEnum.ARTIFICIAL_RESPIRATION]: '人工呼吸',
    [BystanderTreatmentDataTreatmentEnum.CHEST_COMPRESSION]: '胸骨圧迫',
    [BystanderTreatmentDataTreatmentEnum.AIRWAY_OBSTRUCTION_REMOVAL]:
      '口腔内異物除去',
    [BystanderTreatmentDataTreatmentEnum.AED]: 'AED',
    [BystanderTreatmentDataTreatmentEnum.HEMOSTASIS]: '止血',
    [BystanderTreatmentDataTreatmentEnum.OTHER]: 'その他',
  } as const satisfies Record<
    keyof typeof BystanderTreatmentDataTreatmentEnum,
    string
  >;
  const getBystanderTreatmentLabel = createEnumTranslator(
    BYSTANDER_TREATMENT_LABELS
  );

  /**
   * 市民応急処置 実施者のラベル
   */
  const BYSTANDER_TREATMENT_PERSON_LABELS = {
    [BystanderTreatmentDataTreatmentPersonEnum.FAMILY_RELATIVE]: '家族・親族',
    [BystanderTreatmentDataTreatmentPersonEnum.ACQUAINTANCE_COLLEAGUE]:
      '知人・同僚',
    [BystanderTreatmentDataTreatmentPersonEnum.PARAMEDIC]: '救急隊',
    [BystanderTreatmentDataTreatmentPersonEnum.FIREFIGHTER]: '消防隊',
    [BystanderTreatmentDataTreatmentPersonEnum.DOCTOR]: '医師',
    [BystanderTreatmentDataTreatmentPersonEnum.PUBLIC_HEALTH_NURSE]: '保健師',
    [BystanderTreatmentDataTreatmentPersonEnum.MIDWIFE]: '助産師',
    [BystanderTreatmentDataTreatmentPersonEnum.NURSE]: '看護師',
    [BystanderTreatmentDataTreatmentPersonEnum.POLICE_OFFICER]: '警察官',
    [BystanderTreatmentDataTreatmentPersonEnum.HELPER]: 'ヘルパー等',
    [BystanderTreatmentDataTreatmentPersonEnum.TEACHER]: '教職員',
    [BystanderTreatmentDataTreatmentPersonEnum.PASSERBY]: '通行人',
    [BystanderTreatmentDataTreatmentPersonEnum.RELATED_PERSON]: '関係者',
    [BystanderTreatmentDataTreatmentPersonEnum.FIRE_BRIGADE_MEMBER]: '消防団員',
    [BystanderTreatmentDataTreatmentPersonEnum.OTHER]: 'その他',
  } as const satisfies Record<
    keyof typeof BystanderTreatmentDataTreatmentPersonEnum,
    string
  >;

  const getBystanderTreatmentPersonLabel = createEnumTranslator(
    BYSTANDER_TREATMENT_PERSON_LABELS
  );

  /**
   * AED除細動回数ラベル
   */
  const AED_COUNT_LABELS = {
    [BystanderAedCountTypeEnum.ONCE]: '1回',
    [BystanderAedCountTypeEnum.TWICE]: '2回',
    [BystanderAedCountTypeEnum.THREE]: '3回',
    [BystanderAedCountTypeEnum.FOUR]: '4回',
    [BystanderAedCountTypeEnum.FIVE]: '5回',
    [BystanderAedCountTypeEnum.SIX]: '6回',
    [BystanderAedCountTypeEnum.SEVEN]: '7回',
    [BystanderAedCountTypeEnum.EIGHT]: '8回',
    [BystanderAedCountTypeEnum.NINE]: '9回',
    [BystanderAedCountTypeEnum.OVER_TEN]: '10回以上',
  } as const satisfies Record<BystanderAedCountTypeEnum, string>;
  const getAedCountLabel = createEnumTranslator(AED_COUNT_LABELS);

  /**
   * 連携活動系ラベル
   */
  const COLLABORATION_LABELS = {
    [BaseInfoCollaborationEnum.OTHER_EMS_TEAM]: '他の救急隊',
    [BaseInfoCollaborationEnum.FIREFIGHTING_TEAM]: '消防隊',
    [BaseInfoCollaborationEnum.RESCUE_TEAM]: '救助隊',
    [BaseInfoCollaborationEnum.DR_CAR]: 'ドクターカー',
    [BaseInfoCollaborationEnum.DR_HELI]: 'ドクターヘリ',
    [BaseInfoCollaborationEnum.DISASTER_DR_HELI]: '防災ヘリ',
    [BaseInfoCollaborationEnum.OTHER]: 'その他',
  } as const satisfies Record<keyof typeof BaseInfoCollaborationEnum, string>;
  const getCollaborationLabel = createEnumTranslator(COLLABORATION_LABELS);

  const COLLABORATION_OTHER1_LABELS = {
    [BaseInfoCollaborationOther1Enum.PUMP]: 'ポンプ隊',
    [BaseInfoCollaborationOther1Enum.RESCUE_TEAM]: '救助隊',
    [BaseInfoCollaborationOther1Enum.PUMP_AND_RESCUE_TEAM]: 'ポンプ・救助隊',
    [BaseInfoCollaborationOther1Enum.DR_HELI]: 'ドクターヘリ',
    [BaseInfoCollaborationOther1Enum.DISASTER_DR_HELI]: '防災ヘリ',
    [BaseInfoCollaborationOther1Enum.DR_CAR]: 'ドクターカー',
    [BaseInfoCollaborationOther1Enum.OTHER_EMS_TEAM]: '他の救急隊',
    [BaseInfoCollaborationOther1Enum.OTHER]: 'その他',
  } as const satisfies Record<
    keyof typeof BaseInfoCollaborationOther1Enum,
    string
  >;
  const getCollaborationOther1Label = createEnumTranslator(
    COLLABORATION_OTHER1_LABELS
  );

  const COLLABORATION_OTHER2_LABELS = {
    [BaseInfoCollaborationOther2Enum.DR_HELI]: 'ドクターヘリ',
    [BaseInfoCollaborationOther2Enum.DISASTER_DR_HELI]: '防災ヘリ',
    [BaseInfoCollaborationOther2Enum.DR_CAR]: 'ドクターカー',
  } as const satisfies Record<
    keyof typeof BaseInfoCollaborationOther2Enum,
    string
  >;
  const getCollaborationOther2Label = createEnumTranslator(
    COLLABORATION_OTHER2_LABELS
  );

  const COLLABORATION_TIMING_LABELS = {
    [BaseInfoCollaborationTimingEnum.BEFORE_CONTACT]: '接触前',
    [BaseInfoCollaborationTimingEnum.AFTER_CONTACT]: '接触後',
  } as const satisfies Record<
    keyof typeof BaseInfoCollaborationTimingEnum,
    string
  >;
  const getCollaborationTimingLabel = createEnumTranslator(
    COLLABORATION_TIMING_LABELS
  );

  const DR_HELI_REQUESTED_TIMING_LABELS = {
    [BaseInfoDrHeliRequestedTimingEnum.ON_SITE_REQUEST]: '現場要請',
    [BaseInfoDrHeliRequestedTimingEnum.INITIAL_REQUEST]: '初動要請',
  } as const satisfies Record<
    keyof typeof BaseInfoDrHeliRequestedTimingEnum,
    string
  >;
  const getDrHeliRequestedTimingLabel = createEnumTranslator(
    DR_HELI_REQUESTED_TIMING_LABELS
  );

  return {
    getIncidentTypeLabel,
    getIncidentSectionLabel,
    getIncidentActivityTypeLabel,
    getIncidentDispatchStatusLabel,
    getIncidentRoadTypeLabel,
    getIncidentRoadDirectionLabel,
    getIncidentTelegramTargetAddressTypeLabel,
    getEmsTeamMemberPositionLabel,
    getPatientSexLabel,
    getOccupationLabel,
    getADLLabel,
    getADLExcretionLabel,
    getADLCommunicationLabel,
    getTransportBodyPositionLabel,
    getMedicalHistoryLabel,
    getECGWaveformLabel,
    getECGRemarksLabel,
    getMeasureTimingLabel,
    getPupilReactionLabel,
    getO2MeansLabel,
    getInitialECGLabel,
    getFacialAppearanceLabel,
    getFacialExpressionLabel,
    getPositionLabel,
    getAmbulanceWithTrainingLabel,
    getWelfareDistrictLabel,
    getCpaInitialEcgLabel,
    getGeneralTreatmentsLabel,
    getAccompanyingLabel,
    getBystanderTypeLabel,
    getBystanderTreatmentLabel,
    getBystanderTreatmentPersonLabel,
    getAedCountLabel,
    getCollaborationLabel,
    getCollaborationOther1Label,
    getCollaborationOther2Label,
    getCollaborationTimingLabel,
    getDrHeliRequestedTimingLabel,
  };
};

export default useEnumTranslator;
